import React from 'react';
import { GetStaticPaths, GetStaticPathsResult, GetStaticProps } from 'next';

import ArticlePage, { ArticlePageProps } from 'hub/components/ArticlePage';
import { mapPostIntoHeadingTree } from 'hub/components/TableOfContents';
import { localeMapping } from 'lib/contentful/client';
import { fetchAllArticles, fetchArticleBySlug } from 'lib/contentful/hub';
import { INDEX_LOCALES } from 'shared/constants';
import { handleStaticPaths } from 'shared/helpers/handleStaticPaths';
import { fetchRatings } from 'shared/services/trustedShops/fetchRatings';

const POST_SUGGESTIONS_PER_SLIDE = 4;
const POST_SUGGESTION_SLIDE_LIMIT = 3;

function HubPost(props: ArticlePageProps) {
  return <ArticlePage {...props} />;
}

export const getStaticProps: GetStaticProps<ArticlePageProps> = async ({
  params,
  locale,
}) => {
  if (!locale) {
    return {
      notFound: true,
    };
  }

  const slug = params?.slug;

  if (!(locale in localeMapping) || typeof slug !== 'string') {
    return { notFound: true };
  }

  const formattedLocale = localeMapping[locale as keyof typeof localeMapping];

  const [ratings, post] = await Promise.all([
    fetchRatings(),
    fetchArticleBySlug({
      slug,
      locale: formattedLocale,
      suggestedArticlesLimit:
        POST_SUGGESTIONS_PER_SLIDE * POST_SUGGESTION_SLIDE_LIMIT,
    }),
  ]);

  if (!post) {
    return {
      notFound: true,
    };
  }
  const headings = mapPostIntoHeadingTree(post) || [];

  return {
    props: {
      post,
      headings,
      ratings,
    },
  };
};

export const getStaticPaths: GetStaticPaths = handleStaticPaths(async () => {
  const articles = await fetchAllArticles();

  const paths: GetStaticPathsResult['paths'] = [];
  INDEX_LOCALES.forEach((locale) => {
    paths.push(
      ...articles.map((article) => ({
        params: {
          category: article.fields.postCategory.fields.slug,
          slug: article.fields.slug,
        },
        locale,
      }))
    );
  });

  return {
    paths,
    fallback: false,
  };
});

export default HubPost;
